/**
 * FeatureFlagContext - Manages feature flags including isMeridianUser
 *
 * Determines Meridian user status based on email domain or specific email addresses
 */
import React, { createContext, useContext, useMemo } from 'react';

import { useAuth } from './AuthContext';

const MERIDIAN_EMAIL_DOMAINS = ['meridian.ai'];
const MERIDIAN_EMAIL_ALLOWLIST = ['srk247@cornell.edu', 'csna@andrew.cmu.edu'];

interface FeatureFlags {
  isMeridianUser: boolean;
}

interface FeatureFlagContextType extends FeatureFlags {
  hasFeature: (feature: keyof FeatureFlags) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

export function FeatureFlagProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, loading } = useAuth();

  const featureFlags = useMemo<FeatureFlags>(() => {
    // During loading or no user, default to false
    if (loading || !user || !user.email) {
      return { isMeridianUser: false };
    }

    const email = user.email.toLowerCase();
    const emailDomain = email.split('@')[1];

    // Check email domain
    const isDomainMatch = MERIDIAN_EMAIL_DOMAINS.some(
      domain => emailDomain === domain.toLowerCase()
    );

    const isEmailAllowlisted = MERIDIAN_EMAIL_ALLOWLIST.some(
      allowed => email === allowed.toLowerCase()
    );

    console.log('DEBUG:', {
      email,
      isDomainMatch,
      isEmailAllowlisted,
      result: isDomainMatch || isEmailAllowlisted,
    });

    return {
      isMeridianUser: isDomainMatch || isEmailAllowlisted,
    };
  }, [user, loading]);

  const hasFeature = (feature: keyof FeatureFlags) => {
    return featureFlags[feature];
  };

  return (
    <FeatureFlagContext.Provider value={{ ...featureFlags, hasFeature }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Hook to access feature flags
 * @example
 * const { isMeridianUser } = useFeatureFlags();
 */
export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagProvider'
    );
  }
  return context;
}

/**
 * Hook to check if user is a Meridian user
 * @example
 * const isMeridianUser = useIsMeridianUser();
 */
export function useIsMeridianUser() {
  const { isMeridianUser } = useFeatureFlags();
  return isMeridianUser;
}
